/**
 * Imports necessary dependencies and components for the current module.
 * @module
 * @requires React
 * @requires CSmartTable from "@coreui/react-pro"
 * @requires useTranslation from "react-i18next"
 * @requires Card from "react-bootstrap/Card"
 * @requires CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML"
 * @requires Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb"
 * @requires ConfirmModal from "../../components/confirmModal/confirmModel"
 * @requires EmptyCell from "../../components/emptyCell/emptyCell"
 * @requires CreateArtistComponent from "./createArtist/createArtistComponent"
 */
import { CSmartTable } from "@coreui/react-pro";
import DOMPurify from "dompurify";
import React from "react";
import Card from "react-bootstrap/Card";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import EmptyCell from "../../components/emptyCell/emptyCell";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc } from "../../helpers/helper.service";
import CreateArtistComponent from "./createArtist/createArtistComponent";

/**
 * Represents the Artist Management component.
 * @param {{object}} props - The component props.
 * @param {{function}} props.showCreateArtistModal - Function to show the create artist modal.
 * @param {{function}} props.closeCreateArtistModal - Function to close the create artist modal.
 * @param {{function}} props.openCreateArtistModal - Function to open the create artist modal.
 * @param {{object}} props.artistData - The data of the artists.
 * @param {{object}} props.triggerRef - The reference to the trigger element.
 * @param {{object}} props.columns - The columns configuration for the table.
 * @param {{boolean}} props.placeHolderShimer - Flag to indicate if the placeholder shimer is
 */
const ArtistManagementComponent = (props) => {
  /**
   * Destructures the necessary props from the `props` object and assigns them to
   * individual variables for easier access and readability.
   * @param {{object}} props - The props object containing all the necessary properties.
   * @returns None
   */
  const { t } = useTranslation();

  const {
    showCreateArtistModal,
    closeCreateArtistModal,
    openCreateArtistModal,
    artistData,
    triggerRef,
    columns,
    placeHolderShimer,
    onUpdateArtist,
    openDeleteUserModal,
    onTableFilterChange,
    NoDataTitle,
    NoDataDesc,
    noDataDescTwo,
    ClearSearchButton,
    modalName,
    showDeleteUserModal,
    onDeleteUser,
    closeDeleteUserModal,
    smarttableref,
    NoDataFound,
    NoDataAddButtonTitle,
    EmptyDataAddButton,
    createArtistModelLoaderButton,
    handleArtisttextChange,
    artisttextcount,
    clearCreatData,
    createValues,
    updateArtistFunction,
    dimensions,
    breadcrumbHeight,
    searchFilterClear,
    searchValue,
    loading,
    tableContainerRef,
    loaderHtml,
    handleSubmitCreate,
    artistValue,
    artistprofileValue,
    aboutartistValue,
    itemsPerPage,
    uploadThumNailValid,
    validFileTypes,
    emptyDataDesc,
    emptyTitle,
    emptyImg,
    setAboutTextEditorValue,
    aboutTextEditorValue,
    handleUploadThumnailFile,
    handleClearUploadedThumnailFile,
    uploadedDocumentThumnailFile,
    profileImage,
    tabHeaderHeight,
    deleteLoader,
    aboutTouched,
    setAboutTouched,
    emptyDataDescTow,
    iscreateArtistModelLoaderButton,
    instagramUrlValue,
  } = props;

  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  return (
    <React.Fragment>
      <Helmet>
        <title>Artist Management | RizzTix </title>
      </Helmet>
      <div className="pageContent artistManagement">
        <div className="artist">
          <Breadcrumbs
            breadcrumbItem={t("ARTIST")}
            BreadcrumbInner={false}
            BreadcrumbmainTitle={true}
          />
          <Card className="mainCard mainCardWrapper">
            <Card.Body className="p-0 activeTab">
              <div className="mainCardBody">
                <div className="Card-Button">
                  <button
                    color="primary"
                    className="btn-block btn btn-primary fillButton"
                    onClick={() => openCreateArtistModal()}
                  >
                    <i className="bx bxs-plus-circle"></i>
                    {t("ADD_ARTIST")}
                  </button>
                </div>
                {/**
                 * Renders a smart table component with custom column rendering and filtering options.
                 * @param {{boolean}} cleaner - Whether to enable the cleaner feature.
                 * @param {{boolean}} clickableRows - Whether to make the rows clickable.
                 * @param {{Array}} columns - The array of column objects to display in the table.
                 * @param {{boolean}} columnFilter - Whether to enable column filtering.
                 * @param {{Array}} items - The array of items to display in the table.
                 * @param {{number}} itemsPerPage - The number of items to display per page.
                 * @param {{Object}} scopedColumns - An object mapping column names to custom rendering functions.
                 * @param {{function}} onTableFilterChange - The function to call
                 */}
                <CSmartTable
                  ref={smarttableref}
                  cleaner
                  clickableRows
                  columns={columns}
                  columnFilter={false}
                  items={artistData}
                  itemsPerPage={itemsPerPage}
                  scopedColumns={{
                    name: (item) => {
                      const instagramUrl =
                        item?.instagramurl ??
                        item?.instagramUrl ??
                        item?.instagram_url ??
                        "";
                      const ig = String(instagramUrl || "").trim();
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper profilePic">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <img
                              src={resolveCloudinaryImageSrc(
                                item?.image,
                                CLOUDINARY_URL
                              )}
                              alt="img"
                            />
                            {item.name}
                            {ig && /^https?:\/\//i.test(ig) ? (
                              <a
                                href={ig}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ms-2 text-decoration-none"
                                title={t("ARTIST_INSTAGRAM_URL")}
                                aria-label={t("ARTIST_INSTAGRAM_URL")}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i className="bx bxl-instagram font-size-18" />
                              </a>
                            ) : null}
                          </div>
                        </td>
                      );
                    },
                    bio: (item) => {
                      // const htmlString = item?.bio;
                      // const parseHTML = (html) => {
                      //   const doc = new DOMParser().parseFromString(
                      //     html,
                      //     "text/html"
                      //   );
                      //   return doc?.body?.textContent || "";
                      // };
                      // const textContent = parseHTML(htmlString);
                      const sanitizedHtml = DOMPurify.sanitize(item?.bio);

                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {/* {item?.bio[0]?.replace(/(<([^>]+)>)/gi, "")} */}
                            <div
                              dangerouslySetInnerHTML={{
                                __html: sanitizedHtml,
                              }}
                            />
                          </div>
                        </td>
                      );
                    },
                    followers: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.followercount?.length
                              ? item?.followercount?.length
                              : 0}
                          </div>
                        </td>
                      );
                    },
                    status: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="actionWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <button
                              className="editButtton"
                              onClick={(e) => {
                                onUpdateArtist(e, item);
                              }}
                            >
                              <i
                                className="mdi mdi-pencil-outline font-size-24"
                                id="deletetooltip"
                              ></i>
                            </button>
                            <button
                              className="deleteButtton"
                              onClick={(e) => {
                                openDeleteUserModal(e, item);
                              }}
                            >
                              <i
                                className="bx bxs-trash font-size-24"
                                id="deletetooltip"
                              ></i>
                            </button>
                          </div>
                        </td>
                      );
                    },
                  }}
                  tableFilter
                  onTableFilterChange={onTableFilterChange}
                  tableFilterLabel={
                    searchValue?.length > 0 && (
                      <button
                        onClick={searchFilterClear}
                        className="btn-close"
                      ></button>
                    )
                  }
                  tableFilterPlaceholder={t("SEARCH_ARTIST")}
                  tableProps={{
                    className: !placeHolderShimer ? "mainTable" : "mainTable ",
                    responsive: true,
                    striped: false,
                    hover: false,
                  }}
                  noItemsLabel={
                    !placeHolderShimer && (
                      <EmptyCell
                        emptyItems={artistData}
                        noDataTitle={
                          artistData?.length === 0 ? NoDataFound : NoDataTitle
                        }
                        noDataAddButton={
                          artistData?.length === 0 ? true : false
                        }
                        noDataAddButtonTitle={NoDataAddButtonTitle}
                        noDataDesc={NoDataDesc}
                        noDataDescTwo={noDataDescTwo}
                        emptyDataAddButton={EmptyDataAddButton}
                        clearSearchButton={ClearSearchButton}
                        emptyImg={emptyImg}
                        emptyTitle={emptyTitle}
                        emptyDataDesc={emptyDataDesc}
                        emptyDataDescTow={emptyDataDescTow}
                      />
                    )
                  }
                  tableBodyProps={{
                    className: "align-middle Table-Body-scroll",
                    style: {
                      height:
                        dimensions.width > 1279
                          ? dimensions.height -
                            breadcrumbHeight -
                            tabHeaderHeight -
                            (loading && loaderHtml) -
                            200
                          : dimensions.height -
                            breadcrumbHeight -
                            tabHeaderHeight -
                            (loading && loaderHtml) -
                            300,
                    },
                    ref: tableContainerRef,
                  }}
                />
                {loading && <CustomLoadingHTML />}
              </div>
            </Card.Body>
          </Card>
        </div>
        {/* Delete Message Model */}
        <ConfirmModal
          modalTitle={"Delete"}
          modalDescription={t("DELETE_ARTIST_HEADING")}
          isUnderline={false}
          showModal={showDeleteUserModal}
          leftBtnTitle={t("YES_DELETE")}
          isModelIcon={true}
          iconColor={"bx bxs-trash"}
          out
          iconCricle={"iconCricleDelete"}
          leftBtnClass={"ok_Btn"}
          isFirstButton={true}
          isSecondButton={true}
          rightBtnClass="borderButton"
          rightBtnTitle={t("NO_CANCEL")}
          onProcced={(e, item) => {
            onDeleteUser(e, item);
          }}
          rightButtonClick={closeDeleteUserModal}
          loaderButtonShow={deleteLoader}
        />
        <CreateArtistComponent
          artistValue={artistValue}
          aboutartistValue={aboutartistValue}
          artistprofileValue={artistprofileValue}
          modalName={modalName}
          triggerRef={triggerRef}
          uploadThumNailValid={uploadThumNailValid}
          showCreateArtistModal={showCreateArtistModal}
          closeCreateArtistModal={closeCreateArtistModal}
          updateArtistFunction={(form) => updateArtistFunction(form)}
          createArtistModelLoaderButton={createArtistModelLoaderButton}
          handleArtisttextChange={handleArtisttextChange}
          artisttextcount={artisttextcount}
          validFileTypes={validFileTypes}
          clearCreatData={clearCreatData}
          createValues={createValues}
          handleSubmitCreate={handleSubmitCreate}
          setAboutTextEditorValue={setAboutTextEditorValue}
          aboutTextEditorValue={aboutTextEditorValue}
          handleUploadThumnailFile={handleUploadThumnailFile}
          handleClearUploadedThumnailFile={handleClearUploadedThumnailFile}
          uploadedDocumentThumnailFile={uploadedDocumentThumnailFile}
          profileImage={profileImage}
          aboutTouched={aboutTouched}
          setAboutTouched={setAboutTouched}
          instagramUrlValue={instagramUrlValue}
        />
      </div>
    </React.Fragment>
  );
};

export default ArtistManagementComponent;
